import React, { useState, useEffect, useMemo, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { Box, Typography, Stack } from '@mui/material'
import {
  CommonButton,
  CommonSelect,
  CommonSlider,
  CommonAddress,
  CommonDatePicker,
} from '@/components/common'
import { useGetProcedureTypes } from '@/hooks'
import { useDonorContext, useTenantContext } from '@/context'
import { ROUTES, DONOR_PORTAL_SCREEN_CONFIG_CODE } from '@/constants'
import {
  DonorPortalScreenMessage,
  getEligibilityDateHelperText,
} from '@/components/donation'
import {
  getDonateNearYouParams,
  getSuggestedDonationDateFromProcedureEligibility,
  getEarliestSuggestedDonationDateFromEligibilities,
} from '@/utils'

export const DonateNearYou = () => {
  const navigate = useNavigate()
  const { donor, getDonor } = useDonorContext()
  const { tenant, loadingTenant } = useTenantContext()
  const tenantDistanceAppliedRef = useRef(false)
  const { procedureTypes, loadingProcedureTypes, getProcedureTypes } =
    useGetProcedureTypes()
  const [formData, setFormData] = useState({
    address: {},
    procedure_type_id: '',
    date: null,
    distance: 'any',
  })

  useEffect(() => {
    getProcedureTypes()
  }, [])

  useEffect(() => {
    getDonor()
  }, [getDonor])

  useEffect(() => {
    const pid = formData.procedure_type_id
    if (!donor?.procedure_eligibilities?.length) return
    const suggested = pid
      ? getSuggestedDonationDateFromProcedureEligibility(
          donor.procedure_eligibilities,
          pid
        )
      : getEarliestSuggestedDonationDateFromEligibilities(
          donor.procedure_eligibilities
        )
    if (!suggested) return
    setFormData(s => (s.date != null ? s : { ...s, date: suggested }))
  }, [donor?.procedure_eligibilities, formData.procedure_type_id])

  useEffect(() => {
    if (loadingTenant || tenantDistanceAppliedRef.current) return
    tenantDistanceAppliedRef.current = true
    const miles = tenant?.donate_radius_miles
    const distance =
      miles != null && typeof miles === 'number' && miles >= 1 ? miles : 'any'
    setFormData(s => ({ ...s, distance }))
  }, [loadingTenant, tenant])

  const handleSearch = async () => {
    const params = getDonateNearYouParams(formData)
    navigate(`${ROUTES.DONATE_NEAR_YOU_RESULTS}?${params.toString()}`)
  }
  const isDisabled = useMemo(() => {
    const hasValidAddress =
      formData.address &&
      Object.keys(formData.address).length > 0 &&
      formData.address.latitude &&
      formData.address.longitude

    return !formData.date || !formData.distance || !hasValidAddress
  }, [formData])

  return (
    <Stack spacing={2} sx={{ pb: 1 }}>
      <DonorPortalScreenMessage
        code={DONOR_PORTAL_SCREEN_CONFIG_CODE.DONATE_NEAR_YOU_SEARCH}
      />
      <Box>
        <Typography variant='subtitle1' sx={{ mb: 0.5 }}>
          Where are you coming from?
        </Typography>
        <CommonAddress
          id='dny-address'
          label='Address'
          placeholder='Nearest to You'
          value={formData.address}
          setValue={v => setFormData(s => ({ ...s, address: v }))}
        />
      </Box>

      <Box>
        <Typography variant='subtitle1' sx={{ mb: 0.5 }}>
          What do you want to donate?
        </Typography>
        <CommonSelect
          id='dny-procedures'
          label='Procedures'
          placeholder='All Available'
          enableSort={false}
          options={procedureTypes}
          value={formData.procedure_type_id}
          loading={loadingProcedureTypes}
          onChange={v => {
            const procedure_type_id = v || ''
            setFormData(s => {
              const next = { ...s, procedure_type_id }
              if (!procedure_type_id) return next
              const suggested =
                getSuggestedDonationDateFromProcedureEligibility(
                  donor?.procedure_eligibilities,
                  procedure_type_id
                )
              if (suggested) next.date = suggested
              return next
            })
          }}
        />
      </Box>

      <Box>
        <Typography variant='subtitle1' sx={{ mb: 0.5 }}>
          When do you want to donate?
        </Typography>
        <CommonDatePicker
          id='dny-date'
          label='Date'
          value={formData.date}
          onChange={d => setFormData(s => ({ ...s, date: d }))}
          helperText={getEligibilityDateHelperText({
            donorId: donor?.id,
            procedureEligibilities: donor?.procedure_eligibilities,
            procedureTypeId: formData.procedure_type_id,
            date: formData.date,
          })}
        />
      </Box>

      <Box>
        <CommonSlider
          id='dny-distance'
          variant='distance'
          value={formData.distance}
          onChange={v => setFormData(s => ({ ...s, distance: v }))}
        />
      </Box>

      <Box pt={3} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
        <CommonButton
          id='dny-cta'
          label='Find Drives'
          variant='primary'
          color='primary'
          size='large'
          disabled={isDisabled}
          onClick={handleSearch}
        />
      </Box>
    </Stack>
  )
}
export default DonateNearYou
